package com.wintermar.inspection

import app.cash.sqldelight.Query
import app.cash.sqldelight.TransacterImpl
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import kotlin.Any
import kotlin.Boolean
import kotlin.ByteArray
import kotlin.Double
import kotlin.Long
import kotlin.String
import kotlin.collections.Collection
import migrations.AnswerTable

public class AnswerTableQueries(
  driver: SqlDriver,
) : TransacterImpl(driver) {
  public fun <T : Any> getAnswersByInspectionId(inspectionId: String, mapper: (
    answerId: String,
    inspectionId: String,
    questionId: String,
    notes: String?,
    isRequiredFileOrNotes: Boolean?,
    answerDateIso: String?,
    answerOptionId: String?,
    answerText: String?,
    lastUpdated: String?,
    isSubmitted: Boolean?,
    findingCategory: String?,
    fileId: String?,
    fileName: String?,
    fileOriginalName: String?,
    fileBlob: ByteArray?,
    filePath: String?,
    fileSize: Long?,
    fileType: String?,
    fileStatus: String?,
    fileHasGenerateChunk: Boolean?,
    locationId: String?,
    latitude: Double?,
    longitude: Double?,
    locationName: String?,
    selectionId: String?,
    answerSelectionId: String?,
    answerSelectionValue: String?,
  ) -> T): Query<T> = GetAnswersByInspectionIdQuery(inspectionId) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getBoolean(4),
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7),
      cursor.getString(8),
      cursor.getBoolean(9),
      cursor.getString(10),
      cursor.getString(11),
      cursor.getString(12),
      cursor.getString(13),
      cursor.getBytes(14),
      cursor.getString(15),
      cursor.getLong(16),
      cursor.getString(17),
      cursor.getString(18),
      cursor.getBoolean(19),
      cursor.getString(20),
      cursor.getDouble(21),
      cursor.getDouble(22),
      cursor.getString(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getString(26)
    )
  }

  public fun getAnswersByInspectionId(inspectionId: String): Query<GetAnswersByInspectionId> = getAnswersByInspectionId(inspectionId, ::GetAnswersByInspectionId)

  public fun <T : Any> getAnswerReferenceWithIncludeFilesUploadStatus(mapper: (
    answerId: String,
    inspectionId: String,
    questionId: String,
    fileId: String?,
    fileStatus: String?,
  ) -> T): Query<T> = Query(-1_293_888_583, arrayOf("AnswerTable", "AnswerFileTable"), driver, "AnswerTable.sq", "getAnswerReferenceWithIncludeFilesUploadStatus", """
  |SELECT
  |    AT.id AS answerId, AT.inspectionId, AT.questionId,
  |    AFT.id AS fileId, AFT.uploadStatus AS fileStatus
  |FROM AnswerTable AT
  |LEFT JOIN AnswerFileTable AFT ON AT.id = AFT.answerId
  """.trimMargin()) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getString(4)
    )
  }

  public fun getAnswerReferenceWithIncludeFilesUploadStatus(): Query<GetAnswerReferenceWithIncludeFilesUploadStatus> = getAnswerReferenceWithIncludeFilesUploadStatus(::GetAnswerReferenceWithIncludeFilesUploadStatus)

  public fun <T : Any> getAnswerById(id: String, mapper: (
    id: String,
    inspectionId: String,
    questionId: String,
    notes: String?,
    isRequiredFileOrNotes: Boolean?,
    answerDateIso: String?,
    answerOptionId: String?,
    answerText: String?,
    lastUpdated: String?,
    isSubmitted: Boolean?,
    findingCategory: String?,
  ) -> T): Query<T> = GetAnswerByIdQuery(id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getBoolean(4),
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7),
      cursor.getString(8),
      cursor.getBoolean(9),
      cursor.getString(10)
    )
  }

  public fun getAnswerById(id: String): Query<AnswerTable> = getAnswerById(id, ::AnswerTable)

  public fun <T : Any> getAnswerByQuestionId(questionId: String, mapper: (
    id: String,
    inspectionId: String,
    questionId: String,
    notes: String?,
    isRequiredFileOrNotes: Boolean?,
    answerDateIso: String?,
    answerOptionId: String?,
    answerText: String?,
    lastUpdated: String?,
    isSubmitted: Boolean?,
    findingCategory: String?,
  ) -> T): Query<T> = GetAnswerByQuestionIdQuery(questionId) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getBoolean(4),
      cursor.getString(5),
      cursor.getString(6),
      cursor.getString(7),
      cursor.getString(8),
      cursor.getBoolean(9),
      cursor.getString(10)
    )
  }

  public fun getAnswerByQuestionId(questionId: String): Query<AnswerTable> = getAnswerByQuestionId(questionId, ::AnswerTable)

  public fun <T : Any> getByUploadStatuses(uploadStatus: Collection<String?>, mapper: (
    fileId: String,
    answerId: String,
    name: String,
    originalName: String?,
    path: String?,
    fileSize: Long?,
    type: String?,
    blob: ByteArray?,
    uploadStatus: String?,
    hasGenerateChunk: Boolean?,
    questionId: String,
    inspectionId: String,
    chunkId: String?,
    chunkFileName: String?,
    chunkOriginalFileName: String?,
    chunkIndex: Long?,
    chunk: ByteArray?,
    chunkFileSize: Long?,
    chunkSize: Long?,
    totalFiles: Long?,
  ) -> T): Query<T> = GetByUploadStatusesQuery(uploadStatus) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3),
      cursor.getString(4),
      cursor.getLong(5),
      cursor.getString(6),
      cursor.getBytes(7),
      cursor.getString(8),
      cursor.getBoolean(9),
      cursor.getString(10)!!,
      cursor.getString(11)!!,
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getLong(15),
      cursor.getBytes(16),
      cursor.getLong(17),
      cursor.getLong(18),
      cursor.getLong(19)
    )
  }

  public fun getByUploadStatuses(uploadStatus: Collection<String?>): Query<GetByUploadStatuses> = getByUploadStatuses(uploadStatus, ::GetByUploadStatuses)

  public fun <T : Any> getFailedSubmissions(mapper: (
    answerType: String,
    answerId: String,
    inspectionId: String,
    questionId: String,
    notes: String?,
    isRequiredFileOrNotes: Boolean?,
    answerDateIso: String?,
    answerOptionId: String?,
    answerText: String?,
    lastUpdated: String?,
    isSubmitted: Boolean?,
    findingCategory: String?,
    fileId: String?,
    fileName: String?,
    fileOriginalName: String?,
    fileBlob: ByteArray?,
    filePath: String?,
    fileSize: Long?,
    fileType: String?,
    fileStatus: String?,
    fileHasGenerateChunk: Boolean?,
    locationId: String?,
    latitude: Double?,
    longitude: Double?,
    locationName: String?,
    selectionId: String?,
    answerSelectionId: String?,
    answerSelectionValue: String?,
  ) -> T): Query<T> = Query(974_058_126, arrayOf("TemplatePageSectionQuestionTable", "AnswerTable", "AnswerFileTable", "AnswerLocationTable", "AnswerSelectionTable"), driver, "AnswerTable.sq", "getFailedSubmissions", """
  |SELECT
  |    Q.answerType,
  |    AT.id AS answerId, AT.inspectionId, AT.questionId, AT.notes, AT.isRequiredFileOrNotes, AT.answerDateIso, AT.answerOptionId, AT.answerText, AT.lastUpdated, AT.isSubmitted, AT.findingCategory,
  |    AFT.id AS fileId, AFT.name AS fileName, AFT.originalName AS fileOriginalName, AFT.blob AS fileBlob, AFT.path AS filePath, AFT.size AS fileSize, AFT.type AS fileType, AFT.uploadStatus AS fileStatus, AFT.hasGenerateChunk AS fileHasGenerateChunk,
  |    ALT.id AS locationId, ALT.latitude, ALT.longitude, ALT.name AS locationName,
  |    AST.id AS selectionId, AST.answerSelectionId, AST.answerSelectionValue
  |FROM TemplatePageSectionQuestionTable Q
  |INNER JOIN AnswerTable AT ON AT.questionId = Q.id
  |LEFT JOIN AnswerFileTable AFT ON AT.id = AFT.answerId
  |LEFT JOIN AnswerLocationTable ALT ON AT.id = ALT.answerId
  |LEFT JOIN AnswerSelectionTable AST ON AT.id = AST.answerId
  |WHERE AT.isSubmitted = 0
  """.trimMargin()) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getString(1)!!,
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4),
      cursor.getBoolean(5),
      cursor.getString(6),
      cursor.getString(7),
      cursor.getString(8),
      cursor.getString(9),
      cursor.getBoolean(10),
      cursor.getString(11),
      cursor.getString(12),
      cursor.getString(13),
      cursor.getString(14),
      cursor.getBytes(15),
      cursor.getString(16),
      cursor.getLong(17),
      cursor.getString(18),
      cursor.getString(19),
      cursor.getBoolean(20),
      cursor.getString(21),
      cursor.getDouble(22),
      cursor.getDouble(23),
      cursor.getString(24),
      cursor.getString(25),
      cursor.getString(26),
      cursor.getString(27)
    )
  }

  public fun getFailedSubmissions(): Query<GetFailedSubmissions> = getFailedSubmissions(::GetFailedSubmissions)

  /**
   * @return The number of rows updated.
   */
  public fun insertAnswer(
    id: String,
    inspectionId: String,
    questionId: String,
    notes: String?,
    isRequiredFileOrNotes: Boolean?,
    answerDateIso: String?,
    answerOptionId: String?,
    answerText: String?,
    lastUpdated: String?,
    isSubmitted: Boolean?,
    findingCategory: String?,
  ): QueryResult<Long> {
    val result = driver.execute(1_483_029_233, """
        |INSERT INTO AnswerTable(id, inspectionId, questionId, notes, isRequiredFileOrNotes, answerDateIso, answerOptionId, answerText, lastUpdated, isSubmitted, findingCategory)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        |ON CONFLICT(id) DO UPDATE SET
        |   inspectionId=excluded.inspectionId,
        |   questionId=excluded.questionId,
        |   notes=excluded.notes,
        |   isRequiredFileOrNotes=excluded.isRequiredFileOrNotes,
        |   answerDateIso=excluded.answerDateIso,
        |   answerOptionId=excluded.answerOptionId,
        |   answerText=excluded.answerText,
        |   lastUpdated=excluded.lastUpdated,
        |   isSubmitted=excluded.isSubmitted,
        |   findingCategory=excluded.findingCategory
        """.trimMargin(), 11) {
          var parameterIndex = 0
          bindString(parameterIndex++, id)
          bindString(parameterIndex++, inspectionId)
          bindString(parameterIndex++, questionId)
          bindString(parameterIndex++, notes)
          bindBoolean(parameterIndex++, isRequiredFileOrNotes)
          bindString(parameterIndex++, answerDateIso)
          bindString(parameterIndex++, answerOptionId)
          bindString(parameterIndex++, answerText)
          bindString(parameterIndex++, lastUpdated)
          bindBoolean(parameterIndex++, isSubmitted)
          bindString(parameterIndex++, findingCategory)
        }
    notifyQueries(1_483_029_233) { emit ->
      emit("AnswerTable")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun insertReplaceAnswer(
    id: String,
    inspectionId: String,
    questionId: String,
    notes: String?,
    isRequiredFileOrNotes: Boolean?,
    answerDateIso: String?,
    answerOptionId: String?,
    answerText: String?,
    lastUpdated: String?,
    isSubmitted: Boolean?,
    findingCategory: String?,
  ): QueryResult<Long> {
    val result = driver.execute(1_803_590_911, """
        |INSERT OR REPLACE INTO AnswerTable(id, inspectionId, questionId, notes, isRequiredFileOrNotes, answerDateIso, answerOptionId, answerText, lastUpdated, isSubmitted, findingCategory)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 11) {
          var parameterIndex = 0
          bindString(parameterIndex++, id)
          bindString(parameterIndex++, inspectionId)
          bindString(parameterIndex++, questionId)
          bindString(parameterIndex++, notes)
          bindBoolean(parameterIndex++, isRequiredFileOrNotes)
          bindString(parameterIndex++, answerDateIso)
          bindString(parameterIndex++, answerOptionId)
          bindString(parameterIndex++, answerText)
          bindString(parameterIndex++, lastUpdated)
          bindBoolean(parameterIndex++, isSubmitted)
          bindString(parameterIndex++, findingCategory)
        }
    notifyQueries(1_803_590_911) { emit ->
      emit("AnswerTable")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteAnswersByInspectionId(inspectionId: String): QueryResult<Long> {
    val result = driver.execute(-706_153_418, """DELETE FROM AnswerTable WHERE inspectionId = ?""", 1) {
          var parameterIndex = 0
          bindString(parameterIndex++, inspectionId)
        }
    notifyQueries(-706_153_418) { emit ->
      emit("AnswerTable")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public fun deleteAllAnswers(): QueryResult<Long> {
    val result = driver.execute(1_547_195_833, """DELETE FROM AnswerTable""", 0)
    notifyQueries(1_547_195_833) { emit ->
      emit("AnswerTable")
    }
    return result
  }

  private inner class GetAnswersByInspectionIdQuery<out T : Any>(
    public val inspectionId: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("AnswerTable", "AnswerFileTable", "AnswerLocationTable", "AnswerSelectionTable", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("AnswerTable", "AnswerFileTable", "AnswerLocationTable", "AnswerSelectionTable", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(-494_917_153, """
    |SELECT
    |    AT.id AS answerId, AT.inspectionId, AT.questionId, AT.notes, AT.isRequiredFileOrNotes, AT.answerDateIso, AT.answerOptionId, AT.answerText, AT.lastUpdated, AT.isSubmitted, AT.findingCategory,
    |    AFT.id AS fileId, AFT.name AS fileName, AFT.originalName AS fileOriginalName, AFT.blob AS fileBlob, AFT.path AS filePath, AFT.size AS fileSize, AFT.type AS fileType, AFT.uploadStatus AS fileStatus, AFT.hasGenerateChunk AS fileHasGenerateChunk,
    |    ALT.id AS locationId, ALT.latitude, ALT.longitude, ALT.name AS locationName,
    |    AST.id AS selectionId, AST.answerSelectionId, AST.answerSelectionValue
    |FROM AnswerTable AT
    |LEFT JOIN AnswerFileTable AFT ON AT.id = AFT.answerId
    |LEFT JOIN AnswerLocationTable ALT ON AT.id = ALT.answerId
    |LEFT JOIN AnswerSelectionTable AST ON AT.id = AST.answerId
    |WHERE AT.inspectionId = ?
    """.trimMargin(), mapper, 1) {
      var parameterIndex = 0
      bindString(parameterIndex++, inspectionId)
    }

    override fun toString(): String = "AnswerTable.sq:getAnswersByInspectionId"
  }

  private inner class GetAnswerByIdQuery<out T : Any>(
    public val id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("AnswerTable", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("AnswerTable", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(749_347_020, """SELECT AnswerTable.id, AnswerTable.inspectionId, AnswerTable.questionId, AnswerTable.notes, AnswerTable.isRequiredFileOrNotes, AnswerTable.answerDateIso, AnswerTable.answerOptionId, AnswerTable.answerText, AnswerTable.lastUpdated, AnswerTable.isSubmitted, AnswerTable.findingCategory FROM AnswerTable WHERE id = ?""", mapper, 1) {
      var parameterIndex = 0
      bindString(parameterIndex++, id)
    }

    override fun toString(): String = "AnswerTable.sq:getAnswerById"
  }

  private inner class GetAnswerByQuestionIdQuery<out T : Any>(
    public val questionId: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("AnswerTable", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("AnswerTable", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(-1_659_193_774, """SELECT AnswerTable.id, AnswerTable.inspectionId, AnswerTable.questionId, AnswerTable.notes, AnswerTable.isRequiredFileOrNotes, AnswerTable.answerDateIso, AnswerTable.answerOptionId, AnswerTable.answerText, AnswerTable.lastUpdated, AnswerTable.isSubmitted, AnswerTable.findingCategory FROM AnswerTable WHERE questionId = ? ORDER BY inspectionId ASC""", mapper, 1) {
      var parameterIndex = 0
      bindString(parameterIndex++, questionId)
    }

    override fun toString(): String = "AnswerTable.sq:getAnswerByQuestionId"
  }

  private inner class GetByUploadStatusesQuery<out T : Any>(
    public val uploadStatus: Collection<String?>,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("AnswerFileTable", "AnswerTable", "AnswerChunkFileTable", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("AnswerFileTable", "AnswerTable", "AnswerChunkFileTable", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> {
      val uploadStatusIndexes = createArguments(count = uploadStatus.size)
      return driver.executeQuery(null, """
          |SELECT
          |    AF.id AS fileId, AF.answerId, AF.name, AF.originalName, AF.path, AF.size AS fileSize, AF.type, AF.blob, AF.uploadStatus, AF.hasGenerateChunk,
          |    AT.questionId, AT.inspectionId,
          |    AC.id AS chunkId, AC.fileName AS chunkFileName, AC.originalFileName AS chunkOriginalFileName, AC.chunkIndex, AC.chunk, AC.fileSize AS chunkFileSize, AC.chunkSize, AC.totalFiles
          |FROM AnswerFileTable AF
          |INNER JOIN AnswerTable AT ON AT.id = AF.answerId
          |LEFT JOIN AnswerChunkFileTable AC ON AC.answerFileId = AF.id
          |WHERE AF.uploadStatus IN $uploadStatusIndexes
          |ORDER BY AF.name ASC, AC.chunkIndex ASC
          """.trimMargin(), mapper, uploadStatus.size) {
            var parameterIndex = 0
            uploadStatus.forEach { uploadStatus_ ->
              bindString(parameterIndex++, uploadStatus_)
            }
          }
    }

    override fun toString(): String = "AnswerTable.sq:getByUploadStatuses"
  }
}
